import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { ChevronDown } from 'lucide-react';
import type { ApiReadLabel } from '../../services/sensorBoxArrays';

type ReadLabelComboboxProps = {
  id?: string;
  labels: ApiReadLabel[];
  value: string;
  onChange: (labelId: string) => void;
  disabled?: boolean;
};

function formatReadLabel(label: ApiReadLabel): string {
  const unit = label.unitAbbreviation || label.unit;
  return unit ? `${label.readValLabel} (${unit})` : label.readValLabel;
}

function labelMatches(label: ApiReadLabel, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [label.readValLabel, label.unit, label.unitAbbreviation, String(label.ID)]
    .join(' ')
    .toLowerCase()
    .includes(needle);
}

export default function ReadLabelCombobox({
  id,
  labels,
  value,
  onChange,
  disabled,
}: ReadLabelComboboxProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const selected = labels.find((label) => String(label.ID) === value) ?? null;

  useEffect(() => {
    const label = labels.find((item) => String(item.ID) === value);
    if (label) setQuery(formatReadLabel(label));
  }, [value, labels]);

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setShowAll(false);
        if (selected) setQuery(formatReadLabel(selected));
        else setQuery('');
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [selected]);

  const options = useMemo(() => {
    if (showAll || !query.trim()) return labels;
    return labels.filter((label) => labelMatches(label, query));
  }, [labels, query, showAll]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, showAll, open]);

  const commit = (label: ApiReadLabel) => {
    onChange(String(label.ID));
    setQuery(formatReadLabel(label));
    setOpen(false);
    setShowAll(false);
  };

  const resolveTypedValue = (next: string) => {
    const exact = labels.filter(
      (label) =>
        formatReadLabel(label).toLowerCase() === next.trim().toLowerCase() ||
        label.readValLabel.toLowerCase() === next.trim().toLowerCase(),
    );
    if (exact.length === 1) {
      onChange(String(exact[0].ID));
      return;
    }
    onChange('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + (open ? 1 : 0), Math.max(options.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = options[activeIndex];
      if (open && option) commit(option);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      setShowAll(false);
      if (selected) setQuery(formatReadLabel(selected));
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <div className="flex gap-2">
        <input
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          value={query}
          placeholder="Search read labels"
          onFocus={() => {
            setOpen(true);
            setShowAll(false);
          }}
          onChange={(event) => {
            const next = event.target.value;
            setQuery(next);
            setShowAll(false);
            setOpen(true);
            resolveTypedValue(next);
          }}
          onKeyDown={handleKeyDown}
          className="min-w-0 flex-1 h-11 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1 disabled:opacity-60"
        />
        <button
          type="button"
          disabled={disabled}
          aria-label="Show all read labels"
          aria-expanded={open && showAll}
          onClick={() => {
            if (open && showAll) {
              setOpen(false);
              setShowAll(false);
              return;
            }
            setShowAll(true);
            setOpen(true);
          }}
          className="h-11 w-11 shrink-0 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:border-teal-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1 disabled:opacity-60"
        >
          <ChevronDown className={`mx-auto h-4 w-4 transition-transform ${open && showAll ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
        >
          {options.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-500">No read labels match that search.</li>
          ) : (
            options.map((label, index) => {
              const idValue = String(label.ID);
              const active = index === activeIndex;
              const selectedOption = idValue === value;
              return (
                <li key={idValue} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={selectedOption}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => commit(label)}
                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm ${
                      active ? 'bg-teal-50 text-teal-900' : 'text-gray-900'
                    }`}
                  >
                    <span>
                      <span className="font-medium">{label.readValLabel}</span>
                      <span className="text-gray-500">
                        {' '}
                        ({label.unitAbbreviation || label.unit})
                      </span>
                    </span>
                    {selectedOption && <span className="text-xs font-medium text-teal-700">Selected</span>}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
